import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import StateSelect from "./StateSelect";

// Contact roles list - matching the UI app's CONTACT_ROLES
const CONTACT_ROLES = [
  "None",
  "CEO",
  "COO",
  "CFO",
  "Chairman",
  "Vice Chairman",
  "Executive Director",
  "President",
  "Vice President",
  "Secretary",
  "Treasurer",
  "Director",
  "Officer",
  "Board Member",
  "Registered Agent",
  "Admin",
  "Other",
];

export default function ContactDialog({
  open,
  onClose,
  editing,
  formData,
  onFormDataChange,
  onSave,
}) {
  const [emailError, setEmailError] = React.useState('');
  const [firstNameError, setFirstNameError] = React.useState('');
  const [lastNameError, setLastNameError] = React.useState('');
  const [roleError, setRoleError] = React.useState('');
  const [metaDataError, setMetaDataError] = React.useState('');

  // Validate required fields
  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return '';
  };

  // Validate email format (now required)
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    // RFC 5322 compliant email regex (simplified version)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., name@example.com)';
    }
    return '';
  };

  // Handle first name input change with validation
  const handleFirstNameChange = (value) => {
    const error = validateRequired(value, 'First name');
    setFirstNameError(error);
    onFormDataChange({ ...formData, first_name: value });
  };

  // Handle last name input change with validation
  const handleLastNameChange = (value) => {
    const error = validateRequired(value, 'Last name');
    setLastNameError(error);
    onFormDataChange({ ...formData, last_name: value });
  };

  // Handle email input change with validation
  const handleEmailChange = (value) => {
    const error = validateEmail(value);
    setEmailError(error);
    onFormDataChange({ ...formData, email: value });
  };

  // Handle role change with validation
  const handleRoleChange = (value) => {
    const error = validateRequired(value, 'Role');
    setRoleError(error);
    onFormDataChange({ ...formData, role: value });
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Get formatted display value for phone fields
  const getFormattedPhone = (value) => {
    if (!value) return '';
    // If already formatted, return as is; otherwise format it
    if (value.includes('(') || value.includes('-')) {
      return formatPhoneNumber(value);
    }
    return formatPhoneNumber(value);
  };

  // Handle phone number input change
  const handlePhoneChange = (field, value) => {
    // Format for display (store formatted in formData for display)
    const formatted = formatPhoneNumber(value);
    onFormDataChange({ ...formData, [field]: formatted });
  };

  const validateMetaData = (value) => {
    if (!value || value.trim() === '') return '';
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'meta_data must be a JSON object (e.g. {"squarespace_customer_id": "abc123"})';
      }
      return '';
    } catch {
      return 'Invalid JSON';
    }
  };

  const handleMetaDataChange = (value) => {
    const err = validateMetaData(value);
    setMetaDataError(err);
    onFormDataChange({ ...formData, meta_data: value });
  };

  // Validate fields when component opens or formData changes
  React.useEffect(() => {
    // Validate first name
    const firstNameErr = validateRequired(formData.first_name || '', 'First name');
    setFirstNameError(firstNameErr);
    
    // Validate last name
    const lastNameErr = validateRequired(formData.last_name || '', 'Last name');
    setLastNameError(lastNameErr);
    
    // Validate role
    const roleErr = validateRequired(formData.role || '', 'Role');
    setRoleError(roleErr);
    
    // Validate email
    const emailErr = validateEmail(formData.email || '');
    setEmailError(emailErr);
    const metaErr = validateMetaData(formData.meta_data || '');
    setMetaDataError(metaErr);
  }, [formData.first_name, formData.last_name, formData.role, formData.email, formData.meta_data]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="First Name (first_name) (required)"
              value={formData.first_name || ""}
              onChange={(e) => handleFirstNameChange(e.target.value)}
              onBlur={(e) => {
                const error = validateRequired(e.target.value, 'First name');
                setFirstNameError(error);
              }}
              error={!!firstNameError}
              helperText={firstNameError}
              required
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Last Name (last_name) (required)"
              value={formData.last_name || ""}
              onChange={(e) => handleLastNameChange(e.target.value)}
              onBlur={(e) => {
                const error = validateRequired(e.target.value, 'Last name');
                setLastNameError(error);
              }}
              error={!!lastNameError}
              helperText={lastNameError}
              required
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth margin="dense" size="small" required error={!!roleError}>
              <InputLabel>Role (role) (required)</InputLabel>
              <Select
                value={formData.role || ""}
                onChange={(e) => handleRoleChange(e.target.value)}
                onBlur={() => {
                  const error = validateRequired(formData.role || '', 'Role');
                  setRoleError(error);
                }}
                label="Role (role) (required)"
              >
                {CONTACT_ROLES.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
              {roleError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {roleError}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_board_member || false}
                  onChange={(e) => onFormDataChange({ ...formData, is_board_member: e.target.checked })}
                />
              }
              label="Is Contact a Board Member"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Email (email) (required)"
              type="email"
              value={formData.email || ""}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={(e) => {
                const error = validateEmail(e.target.value);
                setEmailError(error);
              }}
              error={!!emailError}
              helperText={emailError}
              required
              margin="dense"
              size="small"
              placeholder="name@example.com"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Phone (phone)"
              value={getFormattedPhone(formData.phone)}
              onChange={(e) => handlePhoneChange('phone', e.target.value)}
              margin="dense"
              size="small"
              placeholder="(XXX) XXX-XXXX"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Mobile (mobile)"
              value={getFormattedPhone(formData.mobile)}
              onChange={(e) => handlePhoneChange('mobile', e.target.value)}
              margin="dense"
              size="small"
              placeholder="(XXX) XXX-XXXX"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: "bold" }}>
              Address
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Street (street)"
              value={formData.street || ""}
              onChange={(e) => onFormDataChange({ ...formData, street: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Street 2 (street_2)"
              value={formData.street_2 || ""}
              onChange={(e) => onFormDataChange({ ...formData, street_2: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="City (city)"
              value={formData.city || ""}
              onChange={(e) => onFormDataChange({ ...formData, city: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <StateSelect
              label="State (state)"
              value={formData.state || ""}
              onChange={(e) => onFormDataChange({ ...formData, state: e.target.value })}
              size="small"
              margin="dense"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="ZIP (zip)"
              value={formData.zip || ""}
              onChange={(e) => onFormDataChange({ ...formData, zip: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Country (country)"
              value={formData.country || ""}
              onChange={(e) => onFormDataChange({ ...formData, country: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: "bold" }}>
              Meta Data (meta_data)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              label="Meta Data (JSON)"
              value={formData.meta_data ?? ""}
              onChange={(e) => handleMetaDataChange(e.target.value)}
              onBlur={(e) => {
                const err = validateMetaData(e.target.value);
                setMetaDataError(err);
              }}
              error={!!metaDataError}
              helperText={metaDataError || 'Optional JSON object (e.g. {"squarespace_customer_id": "abc123"})'}
              margin="dense"
              size="small"
              placeholder='{"squarespace_customer_id": "value"}'
              sx={{
                "& textarea": { fontFamily: "monospace", fontSize: "0.875rem" },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onSave} 
          variant="contained"
          disabled={!!emailError || !!firstNameError || !!lastNameError || !!roleError || !!metaDataError}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
